import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download, RotateCw, Zap } from 'lucide-react';
import { Workbook } from 'exceljs';
import { saveAs } from 'file-saver';
import { Trans, useLingui } from '@lingui/react/macro';
import { msg } from '@lingui/core/macro';
import { toast } from 'sonner';
import { useCurrentUser } from '@/hooks/queries/auth.ts';
import {
  nrgkickTransactionsQueryOptions,
  usersDirectoryQueryOptions,
} from '@/hooks/queries/nrgkick-transactions.ts';
import type { APINrgkickTransaction } from '@/types/index.ts';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DatePicker, dayEndIso, dayStartIso, toDateValue } from '@/components/date-picker.tsx';
import { Field, FieldLabel } from '@/components/ui/field';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { PageContainer } from '@/components/page-container.tsx';
import { FeatureLockedPage } from '@/components/feature-locked.tsx';

export const Route = createFileRoute('/_logged/nrgkick-transactions')({
  component: NrgkickTransactionsRoute,
  staticData: { label: msg`NRGKick charges` },
});

function NrgkickTransactionsRoute() {
  const user = useCurrentUser();
  const enabled = user.features.parkadmin?.feat_nrgkick ?? false;
  return enabled ?
      <NrgkickTransactionsPage />
    : <FeatureLockedPage
        feature='NRGKick'
        icon={Zap}
        title={
          <Trans comment="Title of the full-page teaser shown instead of the NRGKick charges page when the customer's software does not include the NRGKick feature.">
            NRGKick is not enabled
          </Trans>
        }
        description={
          <Trans comment="Description under the teaser title, explaining the NRGKick feature is missing from the customer's software and inviting them to upgrade. 'NRGKick' is a product name, keep untranslated.">
            Your software does not include the NRGKick feature. Upgrade it to track your users'
            NRGKick charging sessions.
          </Trans>
        }
      />;
}

const DAY_MS = 24 * 60 * 60 * 1000;

type AppliedRange = { fromIso: string; toIso: string };

type TransactionRow = {
  id: number;
  deviceSerial: string;
  username: string;
  begin: string;
  end: string;
  energyWh: number;
  totalPrice: string;
};

function shortenSerial(serial: string): string {
  return serial.length > 8 ? `...${serial.slice(-8)}` : serial;
}

function toRow(
  transaction: APINrgkickTransaction,
  usernames: Map<number, string>,
  locale: string,
): TransactionRow {
  const energyKwh = transaction.charged_energy_wh / 1000;
  return {
    id: transaction.id,
    deviceSerial: transaction.device_serial,
    username: usernames.get(transaction.user) ?? String(transaction.user),
    begin: new Date(transaction.charge_begin).toLocaleString(locale),
    end: new Date(transaction.charge_end).toLocaleString(locale),
    energyWh: transaction.charged_energy_wh,
    totalPrice: ((transaction.price_per_kwh * energyKwh) / 100).toFixed(2),
  };
}

const COLUMNS = [
  msg`Tr ID`,
  msg`Device serial`,
  msg`Username`,
  msg`Begin`,
  msg`End`,
  msg`Energy (Wh)`,
  msg`Cost (€)`,
];

function NrgkickTransactionsPage() {
  const { t, i18n } = useLingui();
  const [from, setFrom] = useState(() => toDateValue(new Date(Date.now() - 30 * DAY_MS)));
  const [to, setTo] = useState(() => toDateValue(new Date()));
  const [applied, setApplied] = useState<AppliedRange | null>(null);

  const usersQuery = useQuery(usersDirectoryQueryOptions());
  const transactionsQuery = useQuery({
    ...nrgkickTransactionsQueryOptions(applied?.fromIso ?? '', applied?.toIso ?? ''),
    enabled: applied !== null,
  });

  const rangeInvalid = from === '' || to === '' || to < from;
  const usernames = usersQuery.data ?? new Map<number, string>();
  const rows = (transactionsQuery.data ?? []).map((transaction) =>
    toRow(transaction, usernames, i18n.locale),
  );

  function handleLoad() {
    if (rangeInvalid) return;
    const next: AppliedRange = {
      fromIso: dayStartIso(from),
      toIso: dayEndIso(to),
    };
    if (applied && applied.fromIso === next.fromIso && applied.toIso === next.toIso) {
      void transactionsQuery.refetch();
    } else {
      setApplied(next);
    }
  }

  async function handleExport() {
    try {
      const workbook = new Workbook();
      const worksheet = workbook.addWorksheet('Sheet1');
      worksheet.addRow(COLUMNS.map((column) => t(column)));
      for (const row of rows) {
        worksheet.addRow([
          row.id,
          row.deviceSerial,
          row.username,
          row.begin,
          row.end,
          row.energyWh,
          row.totalPrice,
        ]);
      }
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      saveAs(blob, `nrgkick_charges_${from}_${to}.xlsx`);
    } catch {
      toast.error(t`Could not generate the export. Please try again.`);
    }
  }

  return (
    <PageContainer>
      <Card>
        <CardHeader>
          <CardTitle className='flex items-center gap-2'>
            <Zap className='size-5' />
            <Trans>NRGKick charges</Trans>
          </CardTitle>
        </CardHeader>
        <CardContent className='space-y-4'>
          <div className='flex flex-wrap items-end gap-3'>
            <Field className='w-44'>
              <FieldLabel htmlFor='nrgkick-from'>
                <Trans>From</Trans>
              </FieldLabel>
              <DatePicker id='nrgkick-from' value={from} onChange={setFrom} />
            </Field>
            <Field className='w-44'>
              <FieldLabel htmlFor='nrgkick-to'>
                <Trans>To</Trans>
              </FieldLabel>
              <DatePicker id='nrgkick-to' value={to} onChange={setTo} />
            </Field>
            <Button onClick={handleLoad} disabled={rangeInvalid || transactionsQuery.isFetching}>
              <RotateCw />
              {transactionsQuery.isFetching ? t`Loading…` : t`Load`}
            </Button>
            <Button
              variant='outline'
              onClick={() => void handleExport()}
              disabled={rows.length === 0}
              className='ml-auto'
            >
              <Download />
              <Trans>Download Excel</Trans>
            </Button>
          </div>

          {rangeInvalid ?
            <p className='text-destructive text-sm'>
              <Trans>The end date must be on or after the start date.</Trans>
            </p>
          : null}

          {applied === null ?
            <p className='text-muted-foreground py-6 text-center text-sm'>
              <Trans>Pick a date range and load the transactions.</Trans>
            </p>
          : transactionsQuery.isPending ?
            <div className='space-y-2'>
              {Array.from({ length: 4 }).map((_, index) => (
                <Skeleton key={index} className='h-10 w-full' />
              ))}
            </div>
          : transactionsQuery.isError ?
            <p className='text-muted-foreground py-6 text-center text-sm'>
              <Trans>Could not load transactions.</Trans>
            </p>
          : rows.length === 0 ?
            <p className='text-muted-foreground py-6 text-center text-sm'>
              <Trans>No transactions in this range.</Trans>
            </p>
          : <div className='overflow-hidden rounded-md border **:data-[slot=table-container]:max-h-112 **:data-[slot=table-container]:overflow-y-auto'>
              <Table>
                <TableHeader className='bg-card sticky top-0 z-10 shadow'>
                  <TableRow>
                    <TableHead>
                      <Trans>Tr ID</Trans>
                    </TableHead>
                    <TableHead>
                      <Trans>Device serial</Trans>
                    </TableHead>
                    <TableHead>
                      <Trans>Username</Trans>
                    </TableHead>
                    <TableHead>
                      <Trans>Begin</Trans>
                    </TableHead>
                    <TableHead>
                      <Trans>End</Trans>
                    </TableHead>
                    <TableHead className='text-right'>
                      <Trans>Energy (Wh)</Trans>
                    </TableHead>
                    <TableHead className='text-right'>
                      <Trans>Cost (€)</Trans>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell className='font-medium'>{row.id}</TableCell>
                      <TableCell>
                        <Tooltip>
                          <TooltipTrigger render={<span className='cursor-default' />}>
                            {shortenSerial(row.deviceSerial)}
                          </TooltipTrigger>
                          <TooltipContent>{row.deviceSerial}</TooltipContent>
                        </Tooltip>
                      </TableCell>
                      <TableCell>{row.username}</TableCell>
                      <TableCell className='whitespace-nowrap'>{row.begin}</TableCell>
                      <TableCell className='whitespace-nowrap'>{row.end}</TableCell>
                      <TableCell className='text-right tabular-nums'>{row.energyWh}</TableCell>
                      <TableCell className='text-right tabular-nums'>{row.totalPrice}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          }
        </CardContent>
      </Card>
    </PageContainer>
  );
}
